import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { useToast } from '@/components/ui/Toast';
import {
  WorkspaceOrderedHeaders,
  WorkspaceOrderedMetricCells,
} from '@/components/workspace/WorkspaceOrderedHeaders';
import {
  FilterSearch,
  FilterSelect,
  SelectionCheckbox,
  WorkspaceCheckboxCell,
  WorkspaceMiddleCell,
  ToolbarRow,
  WorkspaceSection,
  WorkspaceTablePagination,
  WorkspaceTableShell,
  tableRowClass,
  workspaceTableClass,
} from '@/components/workspace/WorkspaceSection';
import { WorkspaceTableSelectionActions } from '@/components/workspace/WorkspaceTableSelectionActions';
import { WorkspaceProductIdentityCell } from '@/components/workspace/WorkspaceProductIdentityCell';
import type { WorkspaceDimensionSelectionProps } from '@/components/workspace/workspaceSelectionTypes';
import type { WorkspaceMetricColumnDef } from '@/utils/workspaceAdProductMetrics';
import { ReportColumnPicker } from '@/components/ReportColumnPicker';
import { WorkspaceExportButton } from '@/components/workspace/WorkspaceExportButton';
import { WorkspaceMetricFiltersPopover } from '@/components/workspace/WorkspaceMetricFiltersPopover';
import { useMetricFilters } from '@/components/workspace/useMetricFilters';
import { useColumnOrder } from '@/components/workspace/useColumnOrder';
import { useWorkspaceFrozenLayout } from '@/components/workspace/workspaceStickyColumns';
import { useTableSort } from '@/components/workspace/useTableSort';
import { useServerPagination } from '@/hooks/useServerPagination';
import { useWorkspaceAdProductsPage } from '@/hooks/useWorkspaceProductAdsPages';
import type { EntityCrossFilter } from '@/utils/entityCrossFilter';
import {
  AD_PRODUCT_ENTITY_COLUMNS,
  defaultColumnIds,
  workspaceColumnPickerOptions,
} from '@/components/workspace/workspaceColumnConfig';
import {
  applyWorkspaceSpProductAdStateChange,
  isSupportedProductAd,
  productStatusActionOptions,
  summarizeWorkspaceProductAdStateChange,
} from '@/api/workspaceBulkActions';
import { WorkspaceStateChangeModal } from '@/components/workspace/WorkspaceBulkActionModals';
import { useAppDateRange } from '@/contexts/AppDateRangeContext';
import {
  exportWorkspaceTableCsv,
  fetchAllWorkspaceProductRows,
  workspaceExportColumns,
} from '@/api/workspaceCsvExport';

const AD_PRODUCT_METRIC_COLUMNS: WorkspaceMetricColumnDef[] = [
  { key: 'cost', label: 'Ad Spend', polarity: 'lower_is_better', format: 'currency' },
  { key: 'cpc', label: 'CPC', polarity: 'lower_is_better', format: 'currency' },
  { key: 'ctr', label: 'CTR', polarity: 'higher_is_better', format: 'percent' },
  { key: 'cr', label: 'CR%', polarity: 'higher_is_better', format: 'percent' },
  { key: 'purchases30d', label: 'Orders', polarity: 'higher_is_better', format: 'int' },
  { key: 'impressions', label: 'Impressions', polarity: 'higher_is_better', format: 'int' },
  { key: 'clicks', label: 'Clicks', polarity: 'higher_is_better', format: 'int' },
  { key: 'sales30d', label: 'Sales', polarity: 'higher_is_better', format: 'currency' },
  { key: 'acos', label: 'ACoS', polarity: 'lower_is_better', format: 'percent' },
  { key: 'asp', label: 'ASP', polarity: 'higher_is_better', format: 'currency' },
];

const AD_PRODUCT_COLUMN_IDS = defaultColumnIds(AD_PRODUCT_ENTITY_COLUMNS, AD_PRODUCT_METRIC_COLUMNS);

function compareValues(left: string | number, right: string | number, dir: 'asc' | 'desc') {
  const result =
    typeof left === 'number' && typeof right === 'number'
      ? left - right
      : String(left).localeCompare(String(right), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
  return dir === 'asc' ? result : -result;
}

export function WorkspaceAdProductsTable({
  profileId,
  appliedFilter,
  enabled,
  rangeSubtitle,
  isFiltered,
  selection,
}: {
  profileId: string;
  appliedFilter: EntityCrossFilter;
  enabled: boolean;
  rangeSubtitle: string;
  isFiltered: boolean;
  selection: WorkspaceDimensionSelectionProps;
}) {
  const { showToast } = useToast();
  const { range } = useAppDateRange();
  const exportStart = range.current.startDate;
  const exportEnd = range.current.endDate;
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ENABLED');
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusApplying, setStatusApplying] = useState(false);
  const tableSort = useTableSort({ defaultSortBy: 'product' });
  const metricFilters = useMetricFilters({
    tableId: 'ad-products',
    metrics: AD_PRODUCT_METRIC_COLUMNS,
  });
  const columnOrder = useColumnOrder(AD_PRODUCT_COLUMN_IDS);
  const freezeLayout = useWorkspaceFrozenLayout(
    columnOrder.frozenColumnIds,
    columnOrder.displayOrder.join('\0')
  );

  const resetKey = [
    search,
    statusFilter,
    JSON.stringify(appliedFilter),
    tableSort.sortBy,
    tableSort.sortDir,
    metricFilters.serialized,
    exportStart,
    exportEnd,
  ].join('|');

  const { page, pageSize, setPage, setPageSize } = useServerPagination(resetKey);

  const pageResult = useWorkspaceAdProductsPage({
    profileId,
    appliedFilter,
    page,
    pageSize,
    search,
    status: statusFilter,
    sortBy: tableSort.sortBy,
    sortDir: tableSort.sortDir,
    startDate: exportStart,
    endDate: exportEnd,
    metricFilters: metricFilters.payload,
    enabled,
  });

  const visible = useMemo(() => {
    const baseRows = pageResult.rows;
    return [...baseRows].sort((left, right) => {
      switch (tableSort.sortBy) {
        case 'product':
          return compareValues(left.productName || left.asin || left.rowKey, right.productName || right.asin || right.rowKey, tableSort.sortDir);
        case 'campaign':
          return compareValues(left.campaignName, right.campaignName, tableSort.sortDir);
        case 'agAsins':
          return compareValues(left.agAsins, right.agAsins, tableSort.sortDir);
        case 'status':
          return compareValues(left.status, right.status, tableSort.sortDir);
        default:
          return compareValues(
            left.metrics[tableSort.sortBy as keyof typeof left.metrics] ?? 0,
            right.metrics[tableSort.sortBy as keyof typeof right.metrics] ?? 0,
            tableSort.sortDir
          );
      }
    });
  }, [pageResult.rows, tableSort.sortBy, tableSort.sortDir]);

  const visibleIds = visible.map((r) => r.rowKey);
  const allSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selection.pendingIds.includes(id));

  const subtitleParts = [`${pageResult.totalCount} product ads`, rangeSubtitle, 'by campaign'];
  if (isFiltered) subtitleParts.push('Filtered to selected entities');
  const isTableLoading = pageResult.isPending;

  const handleActionSelect = (actionId: string) => {
    if (actionId === 'change_status') setStatusModalOpen(true);
  };

  const handleConfirmStatusChange = async (state: 'ENABLED' | 'PAUSED') => {
    const selectedIds = new Set(selection.pendingIds);
    const supportedRows = new Map(
      pageResult.catalog
        .filter(
          (row) => selectedIds.has(`${row.adProduct}|${row.adId}`) && isSupportedProductAd(row.adProduct)
        )
        .map((row) => [row.adId, { adId: row.adId, label: row.productName || row.asin || row.adId }])
    );
    const supported = [...supportedRows.values()];
    const skippedCount = Math.max(selection.pendingIds.length - supported.length, 0);
    if (!supported.length) {
      showToast('Only SP ad products are supported for status updates right now.', 'error');
      return;
    }
    setStatusApplying(true);
    try {
      await applyWorkspaceSpProductAdStateChange(profileId, supported, state);
      showToast(
        summarizeWorkspaceProductAdStateChange(
          supported.length,
          skippedCount,
          state,
          'ad product'
        ),
        skippedCount > 0 ? 'error' : 'success'
      );
      setStatusModalOpen(false);
      selection.onClearSelection();
      pageResult.refetch();
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to update ad product status',
        'error'
      );
    } finally {
      setStatusApplying(false);
    }
  };

  return (
    <WorkspaceSection
      title="Ad Products"
      countLabel={subtitleParts.join(' · ')}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <WorkspaceMetricFiltersPopover metrics={AD_PRODUCT_METRIC_COLUMNS} {...metricFilters} />
          <WorkspaceExportButton
            onExport={async () => {
              try {
                const fetched = await fetchAllWorkspaceProductRows({
                  profileId,
                  kind: 'ad-products',
                  appliedFilter: appliedFilter as unknown as Record<string, unknown>,
                  state: statusFilter !== 'ALL' ? statusFilter : undefined,
                  search: search.trim() || undefined,
                  sortBy: tableSort.sortBy,
                  sortDir: tableSort.sortDir,
                  startDate: exportStart,
                  endDate: exportEnd,
                  metricFilters: metricFilters.payload,
                });
                const columns = workspaceExportColumns(
                  columnOrder.visibleIds,
                  AD_PRODUCT_ENTITY_COLUMNS,
                  AD_PRODUCT_METRIC_COLUMNS
                );
                const rows = fetched.rows.map((row) => ({
                  product: row.productName ?? row.asin ?? row.rowKey ?? '',
                  campaign: row.campaignName ?? '',
                  agAsins: row.agAsins ?? '',
                  status: row.status ?? '',
                  metrics:
                    row.metrics && typeof row.metrics === 'object'
                      ? (row.metrics as Record<string, unknown>)
                      : row,
                }));
                exportWorkspaceTableCsv({
                  filenamePrefix: 'workspace_ad_products',
                  startDate: exportStart,
                  endDate: exportEnd,
                  columns,
                  rows,
                });
                if (fetched.truncated) {
                  showToast(
                    `Exported first ${fetched.rows.length.toLocaleString()} of ${fetched.totalCount.toLocaleString()} rows`,
                    'error'
                  );
                } else {
                  showToast(`Exported ${fetched.rows.length.toLocaleString()} rows`, 'success');
                }
              } catch (error) {
                showToast(
                  error instanceof Error ? error.message : 'Failed to export ad products',
                  'error'
                );
              }
            }}
          />
          <ReportColumnPicker
            columns={workspaceColumnPickerOptions(AD_PRODUCT_ENTITY_COLUMNS, AD_PRODUCT_METRIC_COLUMNS)}
            selected={columnOrder.visibleIds}
            onChange={columnOrder.setVisibleIds}
            requiredColumnId="product"
            panelTitle="Visible columns"
          />
        </div>
      }
    >
      <ToolbarRow>
        <div className="flex flex-wrap items-end gap-3">
          <FilterSearch
            id="workspace-ad-products-search"
            label="Ad products"
            value={search}
            onChange={setSearch}
          />
          <FilterSelect
            id="workspace-ad-products-status"
            label="Status"
            value={statusFilter}
            onChange={setStatusFilter}
          >
            <option value="ALL">All</option>
            <option value="ENABLED">Enabled</option>
            <option value="PAUSED">Paused</option>
            <option value="ARCHIVED">Archived</option>
          </FilterSelect>
          <WorkspaceTableSelectionActions
            entityLabel="ad product row"
            selectionCount={selection.selectionCount}
            hasPendingChanges={selection.hasPendingChanges}
            canApply={selection.canApply}
            onApply={selection.onApply}
            onClearSelection={selection.onClearSelection}
            disabled={isTableLoading}
            actions={productStatusActionOptions()}
            onActionSelect={handleActionSelect}
          />
        </div>
      </ToolbarRow>
      <p className="mb-2 text-xs text-foreground-muted">
        Product ads by campaign. Ad metrics populate when product-ad report ingest is available.
      </p>
      <WorkspaceTableShell
        isEmpty={!pageResult.isPending && visible.length === 0}
        isLoading={isTableLoading}
        emptyMessage={
          isFiltered
            ? 'No product ads match the current selection.'
            : 'No product ads for this account.'
        }
        footer={
          <WorkspaceTablePagination
            page={page}
            pageSize={pageSize}
            total={pageResult.totalCount}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            disabled={isTableLoading}
          />
        }
      >
        <table ref={freezeLayout.tableRef} className={workspaceTableClass.table}>
          <thead>
            <tr>
              <WorkspaceCheckboxCell isHeader>
                <SelectionCheckbox
                  checked={allSelected}
                  label="Select all visible ad products"
                  disabled={isTableLoading}
                  onChange={(checked) => selection.onToggleAll(visibleIds, checked)}
                />
              </WorkspaceCheckboxCell>
              <WorkspaceOrderedHeaders
                order={columnOrder.displayOrder}
                entityColumns={AD_PRODUCT_ENTITY_COLUMNS}
                metricColumns={AD_PRODUCT_METRIC_COLUMNS}
                sortBy={tableSort.sortBy}
                sortDir={tableSort.sortDir}
                onSort={tableSort.toggleSort}
                onFreeze={columnOrder.freezeColumn}
                frozenColumnIds={columnOrder.frozenColumnIds}
                pinnedFrozenColumnId={columnOrder.pinnedFrozenColumnId}
                frozenLeftById={freezeLayout.frozenLeftById}
                onDragStartColumn={columnOrder.startDrag}
                onDragHoverColumn={columnOrder.hoverReorder}
                onDropColumn={columnOrder.commitDrop}
                onDragEndColumn={columnOrder.endDrag}
                activeDragColumnId={columnOrder.activeDragColumnId}
                hoverColumnId={columnOrder.hoverColumnId}
                hoverPosition={columnOrder.hoverPosition}
                hoverInsertIndex={columnOrder.hoverInsertIndex}
              />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {visible.map((row) => {
              const selected = selection.pendingIds.includes(row.rowKey);
              return (
                <tr key={row.rowKey} className={tableRowClass(selected)}>
                  <WorkspaceCheckboxCell>
                    <SelectionCheckbox
                      checked={selected}
                      label={`Select ad product ${row.productName}`}
                      disabled={isTableLoading}
                      onChange={(checked) => selection.onToggleId(row.rowKey, checked)}
                    />
                  </WorkspaceCheckboxCell>
                  <WorkspaceOrderedMetricCells
                    order={columnOrder.displayOrder}
                    entityColumns={AD_PRODUCT_ENTITY_COLUMNS}
                    frozenColumnIds={columnOrder.frozenColumnIds}
                    frozenLeftById={freezeLayout.frozenLeftById}
                    metrics={row.metrics as Record<string, number>}
                    previousMetrics={null}
                    compareEnabled={false}
                    isLoading={isTableLoading}
                    metricColumns={AD_PRODUCT_METRIC_COLUMNS}
                    activeDragColumnId={columnOrder.activeDragColumnId}
                    hoverColumnId={columnOrder.hoverColumnId}
                    hoverPosition={columnOrder.hoverPosition}
                    renderEntityCell={(columnId, meta) => {
                      if (columnId === 'product') {
                        return (
                          <WorkspaceProductIdentityCell
                            productName={row.productName}
                            asin={row.asin}
                            imageUrl={row.imageUrl}
                            className={meta.className}
                            style={meta.style}
                          />
                        );
                      }
                      if (columnId === 'campaign') {
                        return (
                          <td style={meta.style} className={`${workspaceTableClass.td} ${meta.className} text-foreground-muted`}>
                            {row.campaignName}
                          </td>
                        );
                      }
                      if (columnId === 'agAsins') {
                        return (
                          <td style={meta.style} className={`${workspaceTableClass.td} ${meta.className} font-mono text-xs text-foreground-muted`}>
                            {row.agAsins}
                          </td>
                        );
                      }
                      if (columnId === 'status') {
                        return (
                          <WorkspaceMiddleCell style={meta.style} className={meta.className} alignToTitle>
                            <Badge variant="neutral">{row.status}</Badge>
                          </WorkspaceMiddleCell>
                        );
                      }
                      return null;
                    }}
                  />
                </tr>
              );
            })}
          </tbody>
        </table>
      </WorkspaceTableShell>
      {statusModalOpen ? (
        <WorkspaceStateChangeModal
          title="Change Ad Product Status"
          entityLabel="ad product"
          selectionCount={selection.pendingIds.length}
          pending={statusApplying}
          onCancel={() => setStatusModalOpen(false)}
          onConfirm={(state) => void handleConfirmStatusChange(state)}
        />
      ) : null}
    </WorkspaceSection>
  );
}
